import { isRecord } from '@/utils/objects';
import type { ConditionFilter, EventNode, GeosetNode, MessageNode } from '../types';
import { parseAttributeCompare } from './parse-attribute';
import { channelLabel, detectChannel } from './parse-message';
import { extractFilters, getOperatorLabel, toNumber, toString } from './parse-helpers';

/**
 * The leaves only a wait until's conditions hold.
 *
 * A wait until stores an activity the person performs while they wait as
 * sibling rows: a `{ type: 'foreign_event', event_name }` marker, then the
 * `foreign_event_attribute` rows that refine it. The marker names an event,
 * a message activity (its `event_type` names an email or an action), or a
 * geofence transition (`event_type: 'geofence'`).
 */

/** Marks a marker packed with its refinement rows. Never on the wire. */
const PACKED_MARKER = 'viewer:packed_foreign_event';

function isMarker(row: unknown): boolean {
  return isRecord(row) && row.type === 'foreign_event' && 'event_name' in row;
}

/** Whether a marker opens a message activity or a geofence transition rather than an event. */
function isMessageTypedMarker(eventTypeValue: unknown): boolean {
  const eventType = toString(eventTypeValue);
  return eventType != null && (eventType.includes('email') || eventType.includes('action') || eventType === 'geofence');
}

function isBareRefinementRow(row: unknown): boolean {
  return isRecord(row) && row.type === 'foreign_event_attribute';
}

function isCompareRefinementRow(row: unknown): boolean {
  return isRecord(row) && row.type === 'attribute_compare' && row.field_source === 'foreign_event_attribute';
}

/**
 * Joins each marker with the refinement rows after it, so the marker reads
 * as one condition. A bare refinement row joins the nearest marker before it.
 * A comparing data filter joins only an event's marker, as only an event
 * takes one, and stays its own row when there is none.
 */
export function packForeignEvents(rows: readonly unknown[]): unknown[] {
  const packed: unknown[] = [];
  let nearest: unknown[] | null = null;
  let eventMarker: unknown[] | null = null;
  for (const row of rows) {
    if (isRecord(row) && isMarker(row)) {
      nearest = [];
      if (!isMessageTypedMarker(row.event_type)) {
        eventMarker = nearest;
      }
      packed.push({ ...row, [PACKED_MARKER]: true, refinements: nearest });
    } else if (nearest != null && isBareRefinementRow(row)) {
      nearest.push(row);
    } else if (eventMarker != null && isCompareRefinementRow(row)) {
      eventMarker.push(row);
    } else {
      packed.push(row);
    }
  }
  return packed;
}

/** A packed marker's refinement rows, each with the field it refines. */
function refinementRows(raw: unknown) {
  const rows = isRecord(raw) && Array.isArray(raw.refinements) ? raw.refinements : [];
  return rows.filter(isRecord).map((row) => ({
    field: toString(row.field),
    operator: toString(row.operator),
    inverse: row.inverse === true,
    value: row.value,
    row,
  }));
}

function geofenceActivity(eventName: string | undefined): GeosetNode['activity'] | null {
  switch (eventName) {
    case 'enter':
      return 'entered';
    case 'dwell':
      return 'dwelled';
    case 'exit':
      return 'exited';
    default:
      return null;
  }
}

/** A geofence transition, or null for one this viewer does not know, which then reads as unknown. */
function parseGeoset(eventName: string | undefined, raw: unknown): GeosetNode | null {
  const activity = geofenceActivity(eventName);
  if (activity == null) {
    return null;
  }
  // Only one plain scope reads as a sentence: a negated or `contains` scope, or a fence refinement, stays unknown.
  const rows = refinementRows(raw);
  const [scope] = rows;
  const geosetId = toNumber(scope?.value);
  if (
    rows.length !== 1 ||
    scope.field !== 'geoset_id' ||
    scope.operator !== 'eq' ||
    scope.inverse ||
    geosetId == null
  ) {
    return null;
  }
  return { type: 'geoset', activity, geosetIds: [geosetId] };
}

/** What a message activity is, in the present tense: the message `is clicked`. */
const PRESENT_ACTIONS = {
  sent: 'is sent',
  delivered: 'is delivered',
  opened: 'is opened',
  clicked: 'is clicked',
  converted: 'converts',
  bounced: 'bounces',
  failed: 'fails to send',
  dropped: 'is suppressed',
  spammed: 'is marked as spam',
  unsubscribed: 'leads to an unsubscribe',
  replied: 'is replied to',
  undeliverable: 'is undeliverable',
  viewed: 'is viewed',
} satisfies Record<string, string>;

/** The activity in the present tense, from the event type's prefix: `clicked_email` is `is clicked`. */
function presentAction(eventType: string): string {
  const prefix = eventType.split('_')[0];
  return Object.entries(PRESENT_ACTIONS).find(([key]) => key === prefix)?.[1] ?? 'is interacted with';
}

function isWildcardMessage(name: string | undefined): boolean {
  return name == null || name === '*' || name.endsWith('_*') || name.startsWith('any_');
}

/** A message name that stands for every message of a campaign: `email-campaign_126`. */
const CAMPAIGN_MESSAGE_PATTERN = /-campaign_(\d+)/;

const LINK_FIELDS = new Set(['href', 'action_name']);
const MACHINE_FIELDS = new Set(['machine', 'prefetched']);

type RefinementRow = ReturnType<typeof refinementRows>[number];

function isLinkRow(row: RefinementRow): boolean {
  return LINK_FIELDS.has(row.field ?? '');
}

function isHumanOnlyRow(row: RefinementRow): boolean {
  return MACHINE_FIELDS.has(row.field ?? '') && row.operator === '!exists';
}

/**
 * A message activity, or null when it holds a refinement the sentence cannot
 * say: anything but one link match and the human-only rows.
 */
function parseMessageActivity(name: string | undefined, eventType: string, raw: unknown): MessageNode | null {
  const channel = detectChannel(eventType, name);
  const rows = refinementRows(raw);
  const links = rows.filter(isLinkRow);
  if (links.length > 1 || rows.some((row) => !isLinkRow(row) && !isHumanOnlyRow(row))) {
    return null;
  }
  const [link] = links;
  const campaignId = toNumber(name?.match(CAMPAIGN_MESSAGE_PATTERN)?.[1]);
  const isAnyMessage = isWildcardMessage(name) || campaignId != null;
  return {
    type: 'message',
    channel,
    displayName: isAnyMessage ? `any ${channelLabel(channel)}` : `the ${channelLabel(channel)} ${name}`,
    campaignId,
    action: presentAction(eventType),
    inverse: false,
    humanOnly: rows.some(isHumanOnlyRow),
    href: toString(link?.value),
    hrefOperator: link?.operator == null ? undefined : getOperatorLabel(link.operator, link.inverse),
  };
}

/**
 * One data filter. A comparing one says what its value reads, such as the
 * journey attribute; null for one this viewer has no words for.
 */
function dataFilter(row: unknown): ConditionFilter | null {
  if (!isRecord(row) || row.type !== 'attribute_compare') {
    return extractFilters([row])?.filters[0] ?? null;
  }
  const compared = parseAttributeCompare(row);
  if (compared == null) {
    return null;
  }
  const { field, operator, value, value2, valueSource } = compared;
  return { field, operator: operator ?? 'is', value, value2, valueSource };
}

/** An event performed while waiting, or null when a data filter cannot be read. */
function parsePerformedEvent(name: string | undefined, raw: unknown): EventNode | null {
  const filters = refinementRows(raw).map((row) => dataFilter(row.row));
  if (filters.some((filter) => filter == null)) {
    return null;
  }
  const readable = filters.filter((filter) => filter != null);
  return {
    type: 'event',
    name: name ?? 'event',
    inverse: false,
    isPerformedWhileWaiting: true,
    filters: readable.length === 0 ? undefined : readable,
    filtersOperator: readable.length === 0 ? undefined : 'and',
  };
}

/** A packed marker, with its refinements: an event, a message activity or a geofence transition. */
export function parseForeignEvent(raw: unknown): EventNode | MessageNode | GeosetNode | null {
  if (!isRecord(raw) || raw[PACKED_MARKER] !== true) {
    return null;
  }
  const name = toString(raw.event_name);
  const eventType = toString(raw.event_type);
  if (eventType === 'geofence') {
    return parseGeoset(name, raw);
  }
  if (eventType != null && (eventType.includes('email') || eventType.includes('action'))) {
    return parseMessageActivity(name, eventType, raw);
  }
  return parsePerformedEvent(name, raw);
}
